import { makeObservable, observable, action, runInAction, computed } from 'mobx';
import { modalStore } from '~/mobx/modalStore';
import { localeStore } from 'src/mobx/localesStore';
import { BUTTON_TITLE } from 'src/constants/main';
import t360IntegrationStore from '~/view/screens/SettingsScreen/store/t360IntegrationStore';

export class TimeConfirmStore {
  @observable timeLeft: number | null = null;
  @observable loading = false;
  private timerId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeObservable(this);
  }

  @action
  openModal(options: {
    title: string;
    message?: string;
    time?: number;
    className?: string;
    onConfirm?: () => Promise<void> | void;
    onCancel?: () => void;
    onConfirmTxt?: string;
    onCancelTxt?: string;
  }) {
    modalStore.openTimeConfirmModal({
      title: options.title,
      message: options.message ?? '',
      time: options.time,
      className: options.className,
      onConfirm: options.onConfirm,
      onCancel: options.onCancel,
      onConfirmTxt: options.onConfirmTxt,
      onCancelTxt: options.onCancelTxt,
    });
    this.init(options.time ?? null);
  }

  @action
  openTimeConfirmModalForFetchTakerAndToYouBranches(): void {
    const promise = t360IntegrationStore.fetchTakerAndToYouBranches();
    this.openModal({
      title: localeStore.t('branch_matching_open_page_popup_header'),
      message: localeStore.t('branch_matching_open_page_popup_hint'),
      time: 5,
      onConfirmTxt: localeStore.t('open_button_txt'),
      onCancelTxt: localeStore.t('txt_cancel'),
      onConfirm: async () => {
        await promise;
        this.handleClose();
        modalStore.openToYouMatchBranchesModal({
          takerBranches: t360IntegrationStore.takerAndToYouBranches?.taker_branches ?? [],
          toYouBranches: t360IntegrationStore.takerAndToYouBranches?.to_you_branches ?? [],
        });
      },
      onCancel: this.handleClose,
    });
  }

  @action
  openTimeConfirmModalForMatchTakerAndToYouBranches(): void {
    this.openModal({
      title: localeStore.t('txt_save_changes'),
      message: localeStore.t('branch_matching_save_changes_popup_hint'),
      time: 10,
      onConfirmTxt: localeStore.t('txt_save'),
      onCancelTxt: localeStore.t('txt_cancel'),
      onConfirm: async () => {
        await t360IntegrationStore.matchTakerAndToYouBranches();
        this.handleClose();
      },
      onCancel: () => {
        this.handleClose();
        modalStore.openToYouMatchBranchesModal({
          takerBranches: t360IntegrationStore.takerAndToYouBranches?.taker_branches ?? [],
          toYouBranches: t360IntegrationStore.takerAndToYouBranches?.to_you_branches ?? [],
        });
      },
    });
  }

  @computed
  get condition(): boolean {
    return this.timeLeft !== null && this.timeLeft > 0;
  }

  @computed
  get confirmButtonTitle(): string {
    const modalData = modalStore.timeConfirmModal;
    if (!modalData) return '';
    if (this.condition && this.timeLeft !== null) {
      const minutes = String(Math.floor(this.timeLeft / 60)).padStart(2, '0');
      const seconds = String(this.timeLeft % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    }
    return localeStore.t(modalData.onConfirmTxt ?? BUTTON_TITLE.YES);
  }

  @action
  init(time: number | null) {
    this.timeLeft = time;
    this.startTimer();
  }

  private startTimer() {
    this.clearTimer();
    if (this.timeLeft && this.timeLeft > 0) {
      this.timerId = setTimeout(() => {
        runInAction(() => {
          if (this.timeLeft && this.timeLeft > 0) {
            this.timeLeft--;
            this.startTimer();
          }
        });
      }, 1000);
    }
  }

  private clearTimer() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  @action
  reset() {
    this.clearTimer();
    this.timeLeft = null;
    this.loading = false;
  }

  @action
  handleClose = () => {
    modalStore.closeTimeConfirmModal();
    this.reset();
  };

  @action
  handleConfirm = async () => {
    const onConfirm = modalStore.timeConfirmModal?.onConfirm;
    if (!onConfirm) {
      this.handleClose();
      return;
    }
    this.loading = true;
    try {
      await onConfirm();
    } finally {
      this.loading = false;
    }
    this.handleClose();
  };

  @action
  handleCancel = () => {
    modalStore.timeConfirmModal?.onCancel?.();
    this.handleClose();
  };
}

export const timeConfirmStore = new TimeConfirmStore();
